import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import RequestSetupModal from '../../components/common/RequestSetupModal';
import {
  getDemoOverview, getDemoStudents, getDemoClasses,
  getDemoPayments, getDemoExpenses, trackDemoView,
} from '../../services/demoPublicService';
import {
  MdPeople, MdPersonAdd, MdFitnessCenter, MdPayments,
  MdCheckCircle, MdHourglassTop, MdWarning, MdReceiptLong,
  MdSchedule, MdBarChart, MdArrowForward, MdShield, MdQrCode,
} from 'react-icons/md';
import {
  FEE_STATUS_BADGE as FEE_STATUS_STYLE,
  FEE_STATUS_LABEL,
} from '../../utils/feeStatus';
import { getBeltStyle } from '../../utils/beltStyle';
import { CLASS_TYPE_PILL as CLASS_TYPE_COLOR } from '../../utils/classType';

const ALLOWED_REFS = ['qr', 'whatsapp', 'facebook', 'instagram', 'messenger', 'direct', 'email', 'other'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString()}`;

const EXPENSE_STATUS_STYLE = {
  approved: 'bg-green-100 text-green-700',
  pending:  'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  rejected: 'bg-red-100 text-red-600',
};

function Pill({ className, children }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function StatCard({ icon: Icon, iconBg, iconColor, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center gap-3">
      <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className={`${iconColor} text-xl`} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide leading-tight">{label}</p>
        <p className="text-xl font-bold text-gray-900 leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 leading-tight">{sub}</p>}
      </div>
    </div>
  );
}

function Skeleton({ h = 'h-16', rows = 3 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={`${h} bg-gray-100 rounded-2xl animate-pulse`} />
      ))}
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'overview',  label: 'Overview',  Icon: MdBarChart    },
  { id: 'students',  label: 'Students',  Icon: MdPeople      },
  { id: 'classes',   label: 'Classes',   Icon: MdFitnessCenter},
  { id: 'payments',  label: 'Payments',  Icon: MdPayments    },
  { id: 'expenses',  label: 'Expenses',  Icon: MdReceiptLong },
];

// ── Tab content components ────────────────────────────────────────────────────
function OverviewTab({ slug, clubName }) {
  const { data, isPending } = useQuery({
    queryKey: ['demo-overview', slug],
    queryFn: () => getDemoOverview(slug).then(r => r.data),
  });

  if (isPending) return <Skeleton rows={4} />;
  const ov = data?.overview || {};
  const fs = ov.feeStats || {};

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={MdPeople}       iconBg="bg-blue-50"   iconColor="text-blue-600"   label="Students"   value={ov.activeStudents ?? 0} sub={`${ov.totalStudents ?? 0} total`} />
        <StatCard icon={MdPersonAdd}    iconBg="bg-yellow-50" iconColor="text-yellow-600" label="Trial Leads" value={ov.trialCount ?? 0} sub="intake pipeline" />
        <StatCard icon={MdFitnessCenter}iconBg="bg-green-50"  iconColor="text-green-600"  label="Classes"    value={ov.classCount ?? 0} sub={`${ov.sessionCount ?? 0} sessions run`} />
        <StatCard icon={MdPayments}     iconBg="bg-primary-light" iconColor="text-primary" label="Fees Paid" value={fs.paid ?? 0} sub={fmtNPR(fs.totalCollected)} />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Fee Collection — This Month</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{fs.paid ?? 0}</p>
            <p className="text-xs text-gray-400 mt-0.5">Paid</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">{fs.pending ?? 0}</p>
            <p className="text-xs text-gray-400 mt-0.5">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{fs.overdue ?? 0}</p>
            <p className="text-xs text-gray-400 mt-0.5">Overdue</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{fs.proof_submitted ?? 0}</p>
            <p className="text-xs text-gray-400 mt-0.5">Proof Sent</p>
          </div>
        </div>
        {(ov.totalStudents ?? 0) > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1.5">
              <span>Collection rate</span>
              <span>{Math.round(((fs.paid ?? 0) / (ov.totalStudents ?? 1)) * 100)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-700"
                style={{ width: `${Math.round(((fs.paid ?? 0) / (ov.totalStudents ?? 1)) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">What you can do with TrainAxis</p>
        <ul className="space-y-2">
          {[
            'Track attendance for every session with QR code scan',
            'Send payment reminders via WhatsApp and email',
            'Manage belt exams and promote students automatically',
            'View monthly revenue, overdue fees, and expenses in one dashboard',
            'Trial student intake pipeline — convert leads to full members',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <MdCheckCircle className="text-green-500 text-base flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StudentsTab({ slug }) {
  const { data, isPending } = useQuery({
    queryKey: ['demo-students', slug],
    queryFn: () => getDemoStudents(slug).then(r => r.data),
  });

  if (isPending) return <Skeleton rows={6} />;
  const students = data?.students || [];

  return (
    <div className="space-y-2">
      {students.map((s, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 flex items-center gap-3">
          <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-gray-600 text-sm">
            {s.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm leading-tight">{s.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <Pill className={getBeltStyle(s.grade)}>{s.grade} Belt</Pill>
              <Pill className={FEE_STATUS_STYLE[s.feeStatus]}>{FEE_STATUS_LABEL[s.feeStatus]}</Pill>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-gray-400">Last 30 days</p>
            <p className="text-sm font-bold text-gray-700">{s.sessionsLast30} sessions</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ClassesTab({ slug }) {
  const { data, isPending } = useQuery({
    queryKey: ['demo-classes', slug],
    queryFn: () => getDemoClasses(slug).then(r => r.data),
  });

  if (isPending) return <Skeleton rows={2} h="h-24" />;
  const classes = data?.classes || [];

  return (
    <div className="space-y-3">
      {classes.map((c, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-gray-800 text-sm">{c.name}</p>
                <Pill className={CLASS_TYPE_COLOR[c.type] || 'bg-gray-100 text-gray-600'}>{c.type}</Pill>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{c.discipline}</p>
            </div>
            {c.maxStudents != null && (
              <div className="text-right flex-shrink-0">
                <p className="text-xs text-gray-400">Capacity</p>
                <p className="text-sm font-bold text-gray-700">Up to {c.maxStudents}</p>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1">
              <MdSchedule className="text-gray-400" />
              {c.startTime} – {c.endTime}
            </span>
            <span className="flex items-center gap-1">
              <MdQrCode className="text-gray-400" />
              {(c.days || []).join(', ')}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PaymentsTab({ slug }) {
  const { data, isPending } = useQuery({
    queryKey: ['demo-payments', slug],
    queryFn: () => getDemoPayments(slug).then(r => r.data),
  });

  if (isPending) return <Skeleton rows={6} />;
  const payments = data?.payments || [];

  const fmtMonth = (m) => {
    if (!m) return '—';
    const [y, mo] = m.split('-');
    return new Date(parseInt(y), parseInt(mo) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-2">
      {payments.map((p, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm leading-tight">{p.studentName}</p>
            <p className="text-xs text-gray-400 mt-0.5">{fmtMonth(p.month)} · {p.method}</p>
          </div>
          <div className="text-right flex-shrink-0 space-y-1">
            <p className="text-sm font-bold text-gray-800">{fmtNPR(p.amount)}</p>
            <Pill className={FEE_STATUS_STYLE[p.status]}>{FEE_STATUS_LABEL[p.status]}</Pill>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExpensesTab({ slug }) {
  const { data, isPending } = useQuery({
    queryKey: ['demo-expenses', slug],
    queryFn: () => getDemoExpenses(slug).then(r => r.data),
  });

  if (isPending) return <Skeleton rows={2} />;
  const expenses = data?.expenses || [];

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const fmtCat  = (c) => (c || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <div className="space-y-2">
      {expenses.map((e, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 flex items-center gap-3">
          <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <MdReceiptLong className="text-amber-600 text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-800 text-sm leading-tight">{e.title}</p>
            <p className="text-xs text-gray-400 mt-0.5">{fmtCat(e.category)} · {fmtDate(e.expenseDate)}</p>
          </div>
          <div className="text-right flex-shrink-0 space-y-1">
            <p className="text-sm font-bold text-gray-800">{fmtNPR(e.amount)}</p>
            <Pill className={EXPENSE_STATUS_STYLE[e.status] || 'bg-gray-100 text-gray-500'}>
              {(e.status || '').charAt(0).toUpperCase() + (e.status || '').slice(1)}
            </Pill>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DemoView() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const trackedRef = useRef(false);

  const rawRef = (searchParams.get('ref') || '').toLowerCase().trim();
  const demoRef = ALLOWED_REFS.includes(rawRef) ? rawRef : (rawRef ? 'other' : '');

  const { data: overviewData, isPending, isError } = useQuery({
    queryKey: ['demo-overview', slug],
    queryFn:  () => getDemoOverview(slug).then(r => r.data),
    retry: false,
  });

  // Fire-and-forget view tracking once the demo loads successfully
  useEffect(() => {
    if (overviewData?.success && !trackedRef.current) {
      trackedRef.current = true;
      trackDemoView(slug, demoRef || 'direct');
    }
  }, [overviewData?.success, slug]); // demoRef is stable for a given page load

  const clubName = overviewData?.club?.name || 'TrainAxis Demo';
  const notAvailable = isError || (!isPending && !overviewData?.success);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Toaster position="top-right" />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-sm">T</span>
              </div>
              <span className="font-black text-gray-900 text-sm hidden sm:inline">TrainAxis</span>
            </Link>
            {!notAvailable && (
              <>
                <span className="text-gray-200 hidden sm:inline">/</span>
                <span className="font-semibold text-gray-700 text-sm truncate">{clubName}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1.5 rounded-full border border-amber-200">
              <MdShield className="text-sm" /> Demo Mode — viewing only
            </span>
          </div>
        </div>
      </header>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">

        {isPending && (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {notAvailable && !isPending && (
          <div className="flex flex-col items-center justify-center py-24 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
              <MdShield className="text-gray-300 text-3xl" />
            </div>
            <h1 className="text-xl font-bold text-gray-700 mb-2">Demo not available</h1>
            <p className="text-gray-400 text-sm max-w-xs leading-relaxed">
              This demo link is not active. Contact the club to request access, or visit the TrainAxis website.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary text-white font-semibold text-sm rounded-xl hover:bg-primary-dark transition-colors"
            >
              Go to TrainAxis <MdArrowForward />
            </Link>
          </div>
        )}

        {!isPending && !notAvailable && (
          <>
            {/* Tab bar */}
            <div className="flex gap-1 overflow-x-auto pb-1 mb-5 -mx-1 px-1">
              {TABS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${
                    activeTab === id
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-100'
                  }`}
                >
                  <Icon className="text-base flex-shrink-0" />
                  {label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {activeTab === 'overview'  && <OverviewTab slug={slug} clubName={clubName} />}
            {activeTab === 'students'  && <StudentsTab slug={slug} />}
            {activeTab === 'classes'   && <ClassesTab  slug={slug} />}
            {activeTab === 'payments'  && <PaymentsTab slug={slug} />}
            {activeTab === 'expenses'  && <ExpensesTab slug={slug} />}
          </>
        )}
      </main>

      {/* ── CTA Footer ─────────────────────────────────────────────────────── */}
      {!notAvailable && !isPending && (
        <footer className="border-t border-gray-100 bg-white">
          <div className="max-w-5xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="font-bold text-gray-800 text-sm">Like what you see?</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Start using TrainAxis for your club — students, fees, and attendance in one place.
              </p>
            </div>
            <button
              onClick={() => setShowSetupModal(true)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary text-white font-bold text-sm rounded-xl hover:bg-primary-dark transition-colors flex-shrink-0 active:opacity-80"
            >
              Request Setup <MdArrowForward />
            </button>
          </div>
        </footer>
      )}

      <RequestSetupModal
        isOpen={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        defaultSource="demo"
        demoSlug={slug}
        demoRef={demoRef}
      />
    </div>
  );
}
